import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/TopBar';
import AddressAutocomplete from '../components/AddressAutocomplete';
import { createClient } from '../api/clientsApi';
import { useToast } from '../context/ToastContext';

function CreateClient() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { register, handleSubmit, formState: { errors } } = useForm();
  const [saving, setSaving] = useState(false);

  // Address is managed separately from react-hook-form, since
  // AddressAutocomplete is a controlled value/onChange component,
  // not a plain input react-hook-form's register() can attach to.
  const [address, setAddress] = useState('');
  const [addressDetails, setAddressDetails] = useState(null);

  const onSubmit = async (data) => {
    setSaving(true);
    try {
      const result = await createClient({ ...data, address });
      if (result.success) {
        showToast('success', 'Client created successfully.');
        navigate(`/clients/${result.client_id}`);
      } else {
        showToast('error', result.message || 'Failed to create client.');
      }
    } catch (err) {
      showToast('error', 'Could not connect to server.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex' }}>
      <Sidebar />
      <div style={{ flex: 1 }}>
        <TopBar title="Add Client" />

        <div style={{ padding: 24, maxWidth: 520 }}>
          <form onSubmit={handleSubmit(onSubmit)} style={styles.card}>
            <div style={styles.sectionTitle}>Company Information</div>

            <label style={styles.label}>Company Name</label>
            <input {...register('name', { required: true })} style={styles.input} />
            {errors.name && <p style={styles.errorText}>Required</p>}

            <div style={{ marginTop: 14 }}>
              <AddressAutocomplete
                label="Address"
                value={address}
                onChange={setAddress}
                onAddressSelect={setAddressDetails}
              />
            </div>

            <label style={styles.label}>Phone</label>
            <input {...register('phone')} style={styles.input} />

            <label style={styles.label}>Email</label>
            <input {...register('email')} type="email" style={styles.input} />

            <div style={styles.divider} />
            <div style={styles.sectionTitle}>Responsible Person</div>

            <label style={styles.label}>Name</label>
            <input {...register('contact_name')} style={styles.input} />

            <label style={styles.label}>Phone</label>
            <input {...register('contact_phone')} style={styles.input} />

            <label style={styles.label}>Email</label>
            <input {...register('contact_email')} type="email" style={styles.input} />

            <div style={styles.buttonRow}>
              <button type="button" onClick={() => navigate('/clients')} style={styles.cancelBtn}>
                Cancel
              </button>
              <button type="submit" disabled={saving} style={styles.submitBtn}>
                {saving ? '...' : 'Create Client'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

const styles = {
  card: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 24,
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: '#374151',
    textTransform: 'uppercase',
    marginBottom: 4,
  },
  label: {
    display: 'block',
    fontSize: 13,
    fontWeight: 600,
    color: '#374151',
    marginBottom: 6,
    marginTop: 14,
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #e5e7eb',
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box',
  },
  errorText: { color: '#dc2626', fontSize: 12, marginTop: 4 },
  divider: { height: 1, backgroundColor: '#f3f4f6', margin: '20px 0 4px' },
  buttonRow: { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24 },
  cancelBtn: {
    padding: '10px 20px',
    borderRadius: 8,
    border: '1px solid #e5e7eb',
    backgroundColor: 'white',
    color: '#374151',
    fontWeight: 600,
    fontSize: 14,
    cursor: 'pointer',
  },
  submitBtn: {
    padding: '10px 20px',
    borderRadius: 8,
    border: 'none',
    backgroundColor: '#2563eb',
    color: 'white',
    fontWeight: 700,
    fontSize: 14,
    cursor: 'pointer',
  },
};

export default CreateClient;